import { useState, useEffect } from 'react';
import { getSettings, updateSettings } from '../lib/api';
import { BASE_URL } from '../lib/env';

const inp = {
  width: '100%', border: '1px solid #e5e7eb', padding: '10px 12px',
  fontSize: 12, outline: 'none', fontFamily: 'var(--font-body)',
  color: 'var(--dark)', background: '#fafafa', boxSizing: 'border-box',
  transition: 'border-color 0.2s',
};
const label = {
  fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase',
  fontWeight: 700, color: 'var(--grey-dark)', display: 'block', marginBottom: 6,
};
const card = { background: '#fff', border: '1px solid #e5e7eb', padding: 24, borderRadius: 2 };

export default function SectionSettings() {
  const [form, setForm] = useState({
    store_name:     '',
    tagline:        '',
    support_email:  '',
    instagram_url:  '',
    wa_number:      '',
    currency:       'INR',
    // Mail settings
    mail_from:      '',
    mail_password:  '',
    mail_from_name: '',
  });
  const [saving,       setSaving]       = useState(false);
  const [saved,        setSaved]        = useState(false);
  const [loading,      setLoading]      = useState(true);
  const [testTo,       setTestTo]       = useState('');
  const [testStatus,   setTestStatus]   = useState('idle'); // idle | sending | ok | error
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    getSettings()
      .then(data => setForm(prev => ({ ...prev, ...data })))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  function onChange(e) {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true); setSaved(false);
    try {
      await updateSettings(form);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      alert('Error saving settings: ' + err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleTestMail(e) {
    e.preventDefault();
    if (!testTo) { alert('Enter a recipient email'); return; }
    setTestStatus('sending');
    try {
      const token = localStorage.getItem('aurora_admin_token') || '';
      const res = await fetch(`${BASE_URL}/admin_panel.php?section=settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ sub: 'test_mail', to: testTo }),
      });
      const data = await res.json();
      setTestStatus(data.ok ? 'ok' : 'error');
      setTimeout(() => setTestStatus('idle'), 4000);
    } catch {
      setTestStatus('error');
      setTimeout(() => setTestStatus('idle'), 4000);
    }
  }

  if (loading) return (
    <p style={{ fontSize: 12, color: 'var(--grey-mid)', padding: 32 }}>Loading settings…</p>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>

      {/* ── Page header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 32, flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(28px,4vw,40px)', fontWeight: 300, color: 'var(--dark)', letterSpacing: '-0.02em', margin: 0 }}>
            Settings
          </h1>
          <p style={{ fontSize: 13, color: 'var(--grey-mid)', marginTop: 6, fontFamily: 'var(--font-body)' }}>
            Manage workspace preferences, store information and email notifications.
          </p>
        </div>
        <button onClick={handleSave} disabled={saving}
          style={{ padding: '10px 28px', background: saving ? 'var(--grey-mid)' : saved ? '#16a34a' : 'var(--dark)', color: '#fff', border: 'none', cursor: saving ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', fontFamily: 'var(--font-body)', transition: 'background 0.2s' }}>
          {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save Changes'}
        </button>
      </div>

      <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

        {/* ── Store Information ── */}
        <div style={card}>
          <h3 style={{ fontSize: 12, fontWeight: 700, color: 'var(--dark)', marginBottom: 20, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            Store Information
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={label}>Store Name</label>
              <input name="store_name" value={form.store_name} onChange={onChange} placeholder="e.g. ALI Luxury" style={inp}
                onFocus={e => e.target.style.borderColor = 'var(--dark)'} onBlur={e => e.target.style.borderColor = '#e5e7eb'} />
              <p style={{ fontSize: 11, color: 'var(--grey-mid)', marginTop: 4 }}>Shown as the large text in the header logo.</p>
            </div>
            <div>
              <label style={label}>Tagline</label>
              <input name="tagline" value={form.tagline} onChange={onChange} placeholder="e.g. Authenticated Luxury. Curated for You" style={inp}
                onFocus={e => e.target.style.borderColor = 'var(--dark)'} onBlur={e => e.target.style.borderColor = '#e5e7eb'} />
            </div>
            <div>
              <label style={label}>Support Email</label>
              <input name="support_email" type="email" value={form.support_email} onChange={onChange} placeholder="e.g. hello@aliluxury.in" style={inp}
                onFocus={e => e.target.style.borderColor = 'var(--dark)'} onBlur={e => e.target.style.borderColor = '#e5e7eb'} />
            </div>
            <div>
              <label style={label}>Instagram URL</label>
              <input name="instagram_url" value={form.instagram_url} onChange={onChange} placeholder="e.g. https://instagram.com/aliluxury" style={inp}
                onFocus={e => e.target.style.borderColor = 'var(--dark)'} onBlur={e => e.target.style.borderColor = '#e5e7eb'} />
            </div>
          </div>
        </div>

        {/* ── WhatsApp & Orders ── */}
        <div style={card}>
          <h3 style={{ fontSize: 12, fontWeight: 700, color: 'var(--dark)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            WhatsApp &amp; Orders
          </h3>
          <p style={{ fontSize: 12, color: 'var(--grey-mid)', marginBottom: 20 }}>
            Customers place orders and send enquiries directly via WhatsApp.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={label}>WhatsApp Number (with country code, no +)</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ padding: '10px 14px', background: '#f3f4f6', border: '1px solid #e5e7eb', fontSize: 13, color: 'var(--grey-dark)', flexShrink: 0 }}>+</div>
                <input name="wa_number" value={form.wa_number} onChange={onChange} placeholder="919550639936" style={{ ...inp }}
                  onFocus={e => e.target.style.borderColor = 'var(--dark)'} onBlur={e => e.target.style.borderColor = '#e5e7eb'} />
              </div>
            </div>
            {form.wa_number && (
              <div style={{ padding: '14px 18px', background: '#f0fdf4', border: '1px solid #bbf7d0', display: 'flex', alignItems: 'center', gap: 12 }}>
                <svg width={18} height={18} fill="#16a34a" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#15803d' }}>WhatsApp Active</div>
                  <div style={{ fontSize: 11, color: '#16a34a' }}>wa.me/{form.wa_number}</div>
                </div>
                <a href={`https://wa.me/${form.wa_number}`} target="_blank" rel="noreferrer"
                  style={{ marginLeft: 'auto', fontSize: 11, color: '#16a34a', fontWeight: 600, textDecoration: 'none' }}>Test →</a>
              </div>
            )}
            <div>
              <label style={label}>Currency</label>
              <div style={{ position: 'relative', maxWidth: 280 }}>
                <select name="currency" value={form.currency} onChange={onChange}
                  style={{ ...inp, cursor: 'pointer', appearance: 'none', paddingRight: 36 }}
                  onFocus={e => e.target.style.borderColor = 'var(--dark)'} onBlur={e => e.target.style.borderColor = '#e5e7eb'}>
                  <option value="INR">INR — Indian Rupee (₹)</option>
                  <option value="USD">USD — US Dollar ($)</option>
                  <option value="AED">AED — UAE Dirham (د.إ)</option>
                </select>
                <div style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--grey-mid)' }}>▾</div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Email Notifications ── */}
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8, flexWrap: 'wrap', gap: 12 }}>
            <div>
              <h3 style={{ fontSize: 12, fontWeight: 700, color: 'var(--dark)', margin: '0 0 6px', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                📧 Email Notifications
              </h3>
              <p style={{ fontSize: 12, color: 'var(--grey-mid)', margin: 0 }}>
                Send automated emails for orders, enquiries, consignments and newsletter signups.
              </p>
            </div>
            {form.mail_from && form.mail_password && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 12px', background: '#dcfce7', border: '1px solid #86efac', borderRadius: 4 }}>
                <svg width={12} height={12} fill="none" stroke="#16a34a" strokeWidth={2.5} viewBox="0 0 24 24"><polyline points="20,6 9,17 4,12"/></svg>
                <span style={{ fontSize: 11, color: '#16a34a', fontWeight: 700 }}>Configured</span>
              </div>
            )}
          </div>

          {/* Info box */}
          <div style={{ background: '#fffbeb', border: '1px solid #fde68a', padding: '14px 18px', marginBottom: 20, borderRadius: 2 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e', marginBottom: 6 }}>⚙ Gmail Setup Guide</div>
            <div style={{ fontSize: 12, color: '#92400e', lineHeight: 1.7 }}>
              1. Use a Gmail address as your sender.<br/>
              2. Enable <strong>2-Step Verification</strong> on your Google account.<br/>
              3. Go to <strong>Google Account → Security → App Passwords</strong>.<br/>
              4. Create an app password for "Mail" and paste it below (16-character code, no spaces).<br/>
              5. <strong>Do NOT use your regular Gmail password</strong> — use only the App Password.
            </div>
            <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer"
              style={{ display: 'inline-block', marginTop: 8, fontSize: 11, color: '#92400e', fontWeight: 700, textDecoration: 'underline' }}>
              Open App Passwords →
            </a>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Sender Name */}
            <div>
              <label style={label}>Sender Name</label>
              <input name="mail_from_name" value={form.mail_from_name} onChange={onChange}
                placeholder="e.g. Aurora Luxury" style={inp}
                onFocus={e => e.target.style.borderColor = 'var(--dark)'} onBlur={e => e.target.style.borderColor = '#e5e7eb'} />
              <p style={{ fontSize: 11, color: 'var(--grey-mid)', marginTop: 4 }}>
                The name customers see in their inbox (e.g. "Aurora Luxury").
              </p>
            </div>

            {/* Gmail address */}
            <div>
              <label style={label}>Gmail Address (Sender &amp; Admin Recipient)</label>
              <input name="mail_from" type="email" value={form.mail_from} onChange={onChange}
                placeholder="yourstore@gmail.com" style={inp}
                onFocus={e => e.target.style.borderColor = 'var(--dark)'} onBlur={e => e.target.style.borderColor = '#e5e7eb'} />
              <p style={{ fontSize: 11, color: 'var(--grey-mid)', marginTop: 4 }}>
                All notification emails will be sent from this address. Admin alerts will also go here.
              </p>
            </div>

            {/* App Password */}
            <div>
              <label style={label}>Gmail App Password</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                <input name="mail_password" type={showPassword ? 'text' : 'password'} value={form.mail_password} onChange={onChange}
                  placeholder="xxxx xxxx xxxx xxxx" style={{ ...inp, flex: 1, borderRight: 'none' }}
                  onFocus={e => e.target.style.borderColor = 'var(--dark)'} onBlur={e => e.target.style.borderColor = '#e5e7eb'} />
                <button type="button" onClick={() => setShowPassword(v => !v)}
                  style={{ padding: '10px 14px', border: '1px solid #e5e7eb', background: '#f9fafb', cursor: 'pointer', fontSize: 12, color: 'var(--grey-mid)', flexShrink: 0, transition: 'all 0.2s' }}>
                  {showPassword ? '🙈' : '👁'}
                </button>
              </div>
              <p style={{ fontSize: 11, color: 'var(--grey-mid)', marginTop: 4 }}>
                16-character App Password from Google. This is stored securely in your database.
              </p>
            </div>

            {/* What triggers emails */}
            <div style={{ background: '#f8f9fa', border: '1px solid #e5e7eb', padding: 16, borderRadius: 2 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--dark)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>
                Automatic Email Triggers
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
                {[
                  { icon: '📧', label: 'Newsletter Signup', desc: 'Welcome email to subscriber + admin alert' },
                  { icon: '🛍', label: 'New Enquiry', desc: 'Admin alert + confirmation to customer' },
                  { icon: '📦', label: 'Consignment Submit', desc: 'Admin alert + confirmation to customer' },
                  { icon: '🔄', label: 'Order Status Change', desc: 'Status update email to customer' },
                ].map(t => (
                  <div key={t.label} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    <span style={{ fontSize: 18, flexShrink: 0 }}>{t.icon}</span>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--dark)', marginBottom: 2 }}>{t.label}</div>
                      <div style={{ fontSize: 11, color: 'var(--grey-mid)', lineHeight: 1.5 }}>{t.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── Test Email ── */}
        {form.mail_from && form.mail_password && (
          <div style={{ ...card, borderColor: '#bbf7d0', background: '#f0fdf4' }}>
            <h3 style={{ fontSize: 12, fontWeight: 700, color: '#15803d', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              🧪 Test Email
            </h3>
            <p style={{ fontSize: 12, color: '#166534', marginBottom: 16 }}>
              Save settings first, then send a test email to verify your configuration.
            </p>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <input type="email" value={testTo} onChange={e => setTestTo(e.target.value)}
                placeholder="test@example.com"
                style={{ ...inp, maxWidth: 300, flex: '1 1 200px', borderColor: '#86efac', background: '#fff' }}
                onFocus={e => e.target.style.borderColor = '#16a34a'} onBlur={e => e.target.style.borderColor = '#86efac'} />
              <button type="button" onClick={handleTestMail} disabled={testStatus === 'sending'}
                style={{
                  padding: '10px 24px', background: testStatus === 'ok' ? '#16a34a' : testStatus === 'error' ? '#ef4444' : '#16a34a',
                  color: '#fff', border: 'none', cursor: testStatus === 'sending' ? 'wait' : 'pointer',
                  fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase',
                  opacity: testStatus === 'sending' ? 0.7 : 1, flexShrink: 0,
                }}>
                {testStatus === 'sending' ? 'Sending…' : testStatus === 'ok' ? '✓ Sent!' : testStatus === 'error' ? '✗ Failed' : 'Send Test →'}
              </button>
            </div>
            {testStatus === 'error' && (
              <p style={{ fontSize: 12, color: '#dc2626', marginTop: 10 }}>
                ✗ Failed to send. Check your Gmail address and App Password, then save settings and try again.
              </p>
            )}
          </div>
        )}

        {/* ── Header Preview ── */}
        <div style={card}>
          <h3 style={{ fontSize: 12, fontWeight: 700, color: 'var(--dark)', marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            Header Preview
          </h3>
          <div style={{ background: 'var(--dark)', padding: '16px 32px', display: 'flex', alignItems: 'center', borderRadius: 2 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {form.tagline && (
                <span style={{ fontSize: 9, letterSpacing: '0.28em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.45)', fontWeight: 500 }}>
                  {form.tagline}
                </span>
              )}
              <span style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 300, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#fff' }}>
                {form.store_name || 'ALI Aurora Luxury'}
              </span>
            </div>
          </div>
        </div>

        {/* ── Save button (bottom) ── */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', paddingBottom: 8 }}>
          <button type="submit" disabled={saving}
            style={{ padding: '12px 40px', background: saving ? 'var(--grey-mid)' : saved ? '#16a34a' : 'var(--dark)', color: '#fff', border: 'none', cursor: saving ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', fontFamily: 'var(--font-body)', transition: 'background 0.2s' }}>
            {saving ? 'Saving…' : saved ? '✓ Changes Saved' : 'Save Changes'}
          </button>
        </div>

      </form>
    </div>
  );
}